/**
 * Shared body-scroll lock for full-screen overlays.
 *
 * Several overlays independently want the page behind them to stop scrolling,
 * and they can nest: the AI assistant launches from a menu item *inside* the
 * open mobile navigation, so both hold the lock at once. When each overlay set
 * `document.body.style.overflow` directly, whichever closed first cleared it
 * for everyone — closing the assistant unlocked the page while the mobile menu
 * was still covering the screen.
 *
 * So ownership is tracked per holder and the body style is only restored once
 * the last one lets go.
 *
 * Holders are kept in a Set keyed by an arbitrary owner token ( a string, or
 * the component instance itself ) rather than counted with an integer, which
 * makes release idempotent: an overlay whose teardown runs after an explicit
 * close releases a lock it no longer holds, and a bare counter would drift
 * negative or unlock while another overlay still needs it.
 *
 * Usage:
 *   import { lockScroll, unlockScroll } from './scroll-lock.js';
 *
 *   lockScroll( 'mobile-nav' );
 *   unlockScroll( 'mobile-nav' );
 */

const owners = new Set();

/**
 * Claim the scroll lock for `owner`. Safe to call repeatedly.
 *
 * @param {*} owner Token identifying the holder.
 */
export function lockScroll( owner ) {
	owners.add( owner );

	document.body.style.overflow = 'hidden';
}

/**
 * Release `owner`'s claim, restoring scrolling only when no holders remain.
 *
 * @param {*} owner Token identifying the holder.
 */
export function unlockScroll( owner ) {
	owners.delete( owner );

	if ( 0 === owners.size ) {
		document.body.style.overflow = '';
	}
}
